/**
 * Persistent "Translating · 02:14" pill, rendered at the navigator-root so
 * the nurse can navigate between assessment sections without losing the
 * session. Draggable — long-press is not required; any pan moves it. Single
 * tap (with no drag) prompts Stop. Hidden when no session is active.
 */
import MaterialCommunityIcons from "@expo/vector-icons/MaterialCommunityIcons";
import { useMemo, useRef, useState } from "react";
import {
    Animated,
    Dimensions,
    Modal,
    PanResponder,
    Pressable,
    StyleSheet,
    Text,
    View
} from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";

import { useLiveTranslation } from "@/lib/live-translation/LiveTranslationProvider";
import { colors, fonts, radius, shadows, spacing } from "@/lib/theme";

const PILL_WIDTH_ESTIMATE = 200;
const PILL_HEIGHT = 36;
const DRAG_THRESHOLD = 6; // px movement before we count it as a drag (not a tap)

function formatDuration(ms: number): string {
    const totalSec = Math.floor(ms / 1000);
    const min = Math.floor(totalSec / 60);
    const sec = totalSec % 60;
    return `${String(min).padStart(2, "0")}:${String(sec).padStart(2, "0")}`;
}

export function LiveTranslationRecordingPill() {
    const ctx = useLiveTranslation();
    const insets = useSafeAreaInsets();
    const pan = useRef(new Animated.ValueXY({ x: 0, y: 0 })).current;
    const offset = useRef({ x: 0, y: 0 }).current;
    const moved = useRef(false);
    const [stopMenuOpen, setStopMenuOpen] = useState(false);

    // Compute the clamp box once per layout — pill can't leave the visible
    // screen. Recomputes when insets change (orientation, status bar size).
    const bounds = useMemo(() => {
        const { width, height } = Dimensions.get("window");
        return {
            // The pill starts horizontally centered (left: 0 with a centered
            // parent). The pan offset is relative to that center, so left/right
            // limits are half-width minus half the pill width, give or take a
            // safe padding.
            xMin: -(width / 2) + PILL_WIDTH_ESTIMATE / 2 + spacing.sm,
            xMax: width / 2 - PILL_WIDTH_ESTIMATE / 2 - spacing.sm,
            yMin: 0,
            yMax: height - insets.top - insets.bottom - PILL_HEIGHT - spacing.lg
        };
    }, [insets.top, insets.bottom]);

    const panResponder = useMemo(
        () =>
            PanResponder.create({
                onStartShouldSetPanResponder: () => true,
                onMoveShouldSetPanResponder: (_e, g) =>
                    Math.abs(g.dx) > DRAG_THRESHOLD || Math.abs(g.dy) > DRAG_THRESHOLD,
                onPanResponderGrant: () => {
                    moved.current = false;
                    pan.setOffset({ x: offset.x, y: offset.y });
                    pan.setValue({ x: 0, y: 0 });
                },
                onPanResponderMove: (_e, g) => {
                    if (Math.abs(g.dx) > DRAG_THRESHOLD || Math.abs(g.dy) > DRAG_THRESHOLD) {
                        moved.current = true;
                    }
                    pan.x.setValue(g.dx);
                    pan.y.setValue(g.dy);
                },
                onPanResponderRelease: (_e, g) => {
                    pan.flattenOffset();
                    const finalX = Math.max(bounds.xMin, Math.min(bounds.xMax, offset.x + g.dx));
                    const finalY = Math.max(bounds.yMin, Math.min(bounds.yMax, offset.y + g.dy));
                    offset.x = finalX;
                    offset.y = finalY;
                    Animated.spring(pan, {
                        toValue: { x: finalX, y: finalY },
                        useNativeDriver: false,
                        bounciness: 0
                    }).start();
                }
            }),
        [bounds, offset, pan]
    );

    if (!ctx.isActive || ctx.isOpen) {
        return null;
    }

    const label = ctx.connectingHint ?? `Translating · ${formatDuration(ctx.durationMs)}`;

    return (
        <View pointerEvents="box-none" style={[styles.container, { top: insets.top + spacing.sm }]}>
            <Animated.View
                style={[
                    styles.pillWrapper,
                    {
                        transform: [{ translateX: pan.x }, { translateY: pan.y }]
                    }
                ]}
                {...panResponder.panHandlers}
            >
                <Pressable
                    style={styles.pill}
                    onPress={() => {
                        // A real tap, not the tail end of a drag.
                        if (moved.current) {
                            moved.current = false;
                            return;
                        }
                        // Don't surface the stop UI while still connecting —
                        // nothing useful to stop yet.
                        if (ctx.connectingHint) {
                            return;
                        }
                        setStopMenuOpen(true);
                    }}
                    accessibilityRole="button"
                    accessibilityLabel="Live translation — tap to pause, drag to move"
                >
                    <View style={styles.dot} />
                    <MaterialCommunityIcons
                        name={ctx.connectingHint ? "microphone" : "pause"}
                        size={14}
                        color={colors.surface}
                    />
                    <Text style={styles.text}>{label}</Text>
                </Pressable>
            </Animated.View>

            <Modal
                visible={stopMenuOpen}
                transparent
                animationType="fade"
                onRequestClose={() => setStopMenuOpen(false)}
            >
                <Pressable style={menuStyles.backdrop} onPress={() => setStopMenuOpen(false)}>
                    <Pressable style={menuStyles.card} onPress={(e) => e.stopPropagation()}>
                        <Pressable
                            style={menuStyles.closeBtn}
                            onPress={() => setStopMenuOpen(false)}
                            hitSlop={12}
                            accessibilityLabel="Cancel"
                        >
                            <MaterialCommunityIcons
                                name="close"
                                size={16}
                                color={colors.textSecondary}
                            />
                        </Pressable>
                        <Pressable
                            style={menuStyles.stopBtn}
                            onPress={() => {
                                setStopMenuOpen(false);
                                void ctx.stopSession();
                            }}
                            accessibilityLabel="Stop translation"
                        >
                            <MaterialCommunityIcons name="stop" size={36} color={colors.surface} />
                        </Pressable>
                        <Text style={menuStyles.durationText}>
                            {formatDuration(ctx.durationMs)}
                        </Text>
                        <Pressable
                            style={menuStyles.changeBtn}
                            onPress={() => {
                                setStopMenuOpen(false);
                                void ctx.changeLanguage();
                            }}
                            accessibilityLabel="Change target language"
                        >
                            <MaterialCommunityIcons
                                name="translate"
                                size={14}
                                color={colors.info600}
                            />
                            <Text style={menuStyles.changeText}>Change language</Text>
                        </Pressable>
                    </Pressable>
                </Pressable>
            </Modal>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        position: "absolute",
        left: 0,
        right: 0,
        alignItems: "center",
        zIndex: 1000
    },
    pillWrapper: {
        // Wrapper so the PanResponder can move via transform without fighting
        // the centered absolute layout.
    },
    pill: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm,
        backgroundColor: colors.error500,
        paddingVertical: spacing.sm,
        paddingHorizontal: spacing.base,
        borderRadius: radius.full,
        height: PILL_HEIGHT,
        ...shadows.md
    },
    dot: {
        width: 8,
        height: 8,
        borderRadius: 4,
        backgroundColor: colors.surface
    },
    text: {
        color: colors.surface,
        fontFamily: fonts.semiBold,
        fontSize: 13
    }
});

const menuStyles = StyleSheet.create({
    backdrop: {
        flex: 1,
        backgroundColor: "rgba(15, 10, 40, 0.45)",
        justifyContent: "center",
        alignItems: "center",
        padding: spacing.lg
    },
    card: {
        backgroundColor: colors.surface,
        borderRadius: radius.xl,
        paddingVertical: spacing.xl,
        paddingHorizontal: spacing["2xl"],
        alignItems: "center",
        gap: spacing.base,
        minWidth: 200,
        ...shadows.lg
    },
    closeBtn: {
        position: "absolute",
        top: spacing.sm,
        right: spacing.sm,
        width: 28,
        height: 28,
        borderRadius: 14,
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: colors.gray100
    },
    stopBtn: {
        width: 72,
        height: 72,
        borderRadius: 36,
        backgroundColor: colors.error500,
        alignItems: "center",
        justifyContent: "center",
        ...shadows.md
    },
    durationText: {
        fontFamily: fonts.semiBold,
        fontSize: 14,
        color: colors.textSecondary
    },
    changeBtn: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.xs,
        paddingTop: spacing.sm
    },
    changeText: {
        fontFamily: fonts.semiBold,
        fontSize: 13,
        color: colors.info600
    }
});
